import { useState } from 'react';
import { FlatList, Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { Divider } from 'react-native-paper';

import { useGetTransactionListScrolling } from '@/api/hooks/paymentAccountsHooks';
import { i18n } from '@/utils/i18n';

import ConfirmWarningModal from '../modals/ConfirmWarningModal';
import DotsLoader from '../DotsLoader';
import NextPageFetchingIndicator from '../NextPageFetchingIndicator';
import Text from '../Text';
import TransactionItemView from './TransactionItemView';

interface TransactionsListProps {
  amountColor: string;
  isNpo: boolean;
}

const TransactionsList = ({ amountColor, isNpo }: TransactionsListProps) => {
  const { height } = useWindowDimensions();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState<string | undefined>();
  const [confirmLabel, setConfirmLabel] = useState<string | undefined>();
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const { data, isPending, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage, refetch, isRefetching } =
    useGetTransactionListScrolling({
      limit: 10
    });

  const transactions = data?.pages.flatMap((page) => page.results);

  if (isPending)
    return (
      <View style={styles.contentStyle}>
        <DotsLoader />
      </View>
    );

  return (
    // For Web Height - top header
    <View style={[styles.contentStyle, Platform.OS === 'web' ? { height: height - 69 } : { flex: 1 }]}>
      <FlatList
        style={styles.fullWidth}
        showsVerticalScrollIndicator={false}
        data={transactions}
        scrollEnabled={true}
        keyExtractor={(item) => item.transactionId}
        onEndReachedThreshold={0.35}
        onEndReached={(_) => {
          hasNextPage && !isFetching && fetchNextPage();
        }}
        onRefresh={refetch}
        refreshing={isRefetching}
        renderItem={({ item }) => (
          <TransactionItemView
            transactionItem={item}
            amountColor={amountColor}
            isNpo={isNpo}
            onDelete={refetch}
            onActionSelected={({ message, confirmLabel: label, onConfirm }) => {
              setConfirmationMessage(message);
              setConfirmLabel(label);
              setPendingAction(() => onConfirm);
              setShowConfirmationModal(true);
            }}
          />
        )}
        ItemSeparatorComponent={() => <Divider style={styles.divider} />}
        ListFooterComponent={isFetchingNextPage ? <NextPageFetchingIndicator /> : null}
        ListEmptyComponent={<Text style={styles.emptyComponent}>{i18n.t('settings.transactions.empty')}</Text>}
      />
      <ConfirmWarningModal
        message={confirmationMessage}
        confirmLabel={confirmLabel}
        showPicker={showConfirmationModal}
        loading={false}
        onClosePicker={() => setShowConfirmationModal(false)}
        onConfirmTapped={() => {
          pendingAction?.();
          setShowConfirmationModal(false);
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  contentStyle: {
    justifyContent: 'center',
    alignItems: 'center'
  },

  fullWidth: {
    width: '100%'
  },

  divider: {
    marginVertical: 15
  },

  emptyComponent: {
    paddingHorizontal: 18,
    alignSelf: 'center',
    marginVertical: 25
  }
});

export default TransactionsList;
